/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import React, { useContext, useLayoutEffect } from 'react';
import { DATA } from '../../../constants/messageTypes';
import { DataContext } from '../../../contexts/DataContext';
import { handleKeyDownSubmission } from '../../../utils/cs-helpers';
import { disableAllCardActions, sendMessageToBot } from '../../../utils/messaging-helpers';
import LocationResultCardInfo from './LocationResultCardInfo';
import LocationResultCardMarker from './LocationResultCardMarker';

const LocationResultCard = ({ location, locationIndex, goToLocation, withMargin, language }) => {
  const SELECTED_FILL = 'var(--tertiary-brand-color)';
  const NORMAL_FILL = 'var(--primary-brand-color)';
  const { selectedLocationIndex, setSelectedLocationIndex } = useContext(DataContext);

  useLayoutEffect(() => {
    setSelectedLocationIndex(null);
  }, []);

  useLayoutEffect(() => {
    if (isLocationSelected()) {
      // Small delay ensures carousel is ready to scroll
      setTimeout(() => {
        goToLocation(locationIndex);
      }, 0);
    }
  }, [selectedLocationIndex, locationIndex, goToLocation]);

  const findAndSubmitClinicForLocation = () => {
    disableAllCardActions();
    sendMessageToBot(DATA, {
      // TODO: send the action to get ID dynamically
      data: { clinicId: location?.clinic?.id },
      id: 'select_location'
    });
  };

  const handleCardSelection = () => {
    if (selectedLocationIndex !== null && selectedLocationIndex !== locationIndex) {
      // Different location selected - switch to this one
      setSelectedLocationIndex(locationIndex);
    } else {
      // Same or none selected - toggle current
      const nextSelectedIndex = isLocationSelected() ? null : locationIndex;
      setSelectedLocationIndex(nextSelectedIndex);
    }
  };

  const handleCardSubmission = event => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (!isLocationSelected()) {
      setSelectedLocationIndex(locationIndex);
    }
    findAndSubmitClinicForLocation();
  };

  const isLocationSelected = () => selectedLocationIndex === locationIndex;

  return (
    <div
      style={withMargin ? { marginRight: '8px' } : null}
      className={isLocationSelected() ? 'location-wrapper selected' : 'location-wrapper'}
      role="button"
      tabIndex="0"
      onClick={handleCardSelection}
      onKeyDown={e => handleKeyDownSubmission(e, handleCardSelection, locationIndex)}
    >
      <LocationResultCardMarker
        pathFill={isLocationSelected() ? SELECTED_FILL : NORMAL_FILL}
        locationIndex={locationIndex}
      />
      <LocationResultCardInfo
        location={location}
        handleCardSubmission={handleCardSubmission}
        isDisabled={false}
        language={language}
      />
    </div>
  );
};

export default React.memo(LocationResultCard);
